import { action, computed, flow, makeObservable, observable } from "mobx";
import moment from "moment";
import { apiFetcher } from "../utils/fetch";
import { updateItems } from "./utils";

export default class Tenancy {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.selected = null;
    this.items = [];
    this.loading = false;
    this.error = null;

    makeObservable(this, {
      selected: observable,
      items: observable,
      loading: observable,
      error: observable,

      hasTenancy: computed,
      currentStatus: computed,
      isConcludedTenancy: computed,
      isCurrentTenancy: computed,

      setSelected: action,
      setItems: action,
      setLoading: action,
      setError: action,

      fetchByTenantId: flow,
      fetchOne: flow,
      create: flow,
      update: flow,
    });
  }

  setSelected = (tenancy) => (this.selected = tenancy);
  setItems = (items) => (this.items = items);
  setLoading = (loading) => (this.loading = loading);
  setError = (error) => (this.error = error);

  get hasTenancy() {
    return !!this.selected && (!!this.selected._id || !!this.selected.roomId);
  }

  get currentStatus() {
    const rawStatus = this.selected?.status || this.rootStore?.tenant?.selected?.status || "";
    return rawStatus.toUpperCase();
  }

  get isConcludedTenancy() {
    return ["ENDED", "CANCELLED", "STOPPED"].includes(this.currentStatus);
  }

  get isCurrentTenancy() {
    return ["BOOKED", "ACTIVE", "INPROGRESS", "END_SOON"].includes(this.currentStatus);
  }

  *fetchByTenantId(tenantId) {
    this.setLoading(true);
    this.setError(null);

    const tenant = this.rootStore?.tenant?.selected || {};
    const tId = tenantId || tenant._id;

    if (!tId) {
      this.setSelected(null);
      this.setItems([]);
      this.setLoading(false);
      return { status: 400, error: "Tenant ID required" };
    }

    try {
      let tenancies = [];
      try {
        const res = yield apiFetcher().get("/tenancies", {
          params: { tenantId: tId },
        });
        if (Array.isArray(res.data)) {
          tenancies = res.data;
        }
      } catch (err) {
        // Backend tenancies endpoint fallback
      }

      if (tenancies.length > 0) {
        this.setItems(tenancies);
        // Prioritize active or booked tenancy as the selected one
        const active = tenancies.find((t) =>
          ["BOOKED", "ACTIVE", "INPROGRESS", "END_SOON"].includes((t.status || "").toUpperCase())
        );
        this.setSelected(active || tenancies[0]);
      } else {
        // Fallback: construct tenancy context from tenant object if tenant has a room/contract
        const hasRoom = !!tenant.room || !!tenant.room?.roomId || !!tenant.room?._id;
        const hasOccupancyHistory = Array.isArray(tenant.occupancyHistory) && tenant.occupancyHistory.length > 0;

        if (hasRoom || hasOccupancyHistory) {
          let derivedStatus = "ACTIVE";
          const tStatus = (tenant.status || "").toLowerCase();
          if (tStatus === "booked") derivedStatus = "BOOKED";
          else if (tStatus === "stopped" || tStatus === "ended") derivedStatus = "ENDED";
          else if (tStatus === "cancelled") derivedStatus = "CANCELLED";
          else if (tStatus === "inprogress" || tStatus === "end_soon") derivedStatus = "ACTIVE";
          else if (tenant.status) derivedStatus = tenant.status.toUpperCase();

          const fallbackTenancy = {
            _id: tenant.tenancyId || `tenancy_${tId}`,
            tenantId: tId,
            roomId: tenant.room?.roomId || tenant.room?._id || null,
            roomName: tenant.room?.name || "-",
            propertyId: tenant.property?.propertyId || tenant.property?._id || null,
            propertyName: tenant.property?.propertyName || "-",
            startDate: tenant.beginDate ? moment(tenant.beginDate, "DD/MM/YYYY").toDate() : new Date(),
            endDate: tenant.endDate ? moment(tenant.endDate, "DD/MM/YYYY").toDate() : null,
            status: derivedStatus,
            rent: tenant.rent || 0,
          };
          this.setItems([fallbackTenancy]);
          this.setSelected(fallbackTenancy);
        } else {
          this.setItems([]);
          this.setSelected(null);
        }
      }

      this.setLoading(false);
      return { status: 200, data: this.selected };
    } catch (error) {
      console.error("[Tenancy] fetchByTenantId error:", error);
      this.setError("Unable to load tenancy");
      this.setLoading(false);
      return { status: 500, error };
    }
  }

  *fetchOne(tenancyId) {
    this.setLoading(true);
    try {
      const res = yield apiFetcher().get(`/tenancies/${tenancyId}`);
      if (res.data) {
        this.setSelected(res.data);
        this.setItems(updateItems(res.data, this.items));
      }
      this.setLoading(false);
      return { status: 200, data: res.data };
    } catch (error) {
      this.setLoading(false);
      return { status: error?.response?.status || 500, error };
    }
  }

  *create(tenancyPayload) {
    this.setLoading(true);
    try {
      const res = yield apiFetcher().post("/tenancies", tenancyPayload);
      if (res.data) {
        this.setSelected(res.data);
        this.setItems(updateItems(res.data, this.items));
      }
      this.setLoading(false);
      return { status: 200, data: res.data };
    } catch (error) {
      this.setLoading(false);
      return { status: error?.response?.status || 500, error };
    }
  }

  *update(tenancyId, tenancyPayload) {
    this.setLoading(true);
    try {
      const res = yield apiFetcher().patch(`/tenancies/${tenancyId}`, tenancyPayload);
      if (res.data) {
        this.setSelected(res.data);
        this.setItems(updateItems(res.data, this.items));
      }
      this.setLoading(false);
      return { status: 200, data: res.data };
    } catch (error) {
      this.setLoading(false);
      return { status: error?.response?.status || 500, error };
    }
  }
}
